import { graphql, useStaticQuery } from "gatsby";
import { hasConfiguredTags } from "../utils/consent";

export type ConsentLabels = {
  message: string;
  privacyLink: string;
  accept: string;
  reject: string;
  settings: string;
  close: string;
  privacyPolicy: PrivacyPolicyNode;
};

type ConsentNode = {
  fields: { language: string };
  frontmatter: {
    message: string;
    privacy_link: string;
    accept: string;
    reject: string;
    settings: string;
    close: string | null;
  };
};

type PrivacyPolicyNode = {
  fields: { language: string };
  frontmatter: { title: string };
  html: string;
};

// The consent banner is mounted from Gatsby's browser/SSR wrappers, where page
// queries are unavailable. A static query keeps its copy in the content repo
// while making every localized block available to that global component.
// Null when the copy is missing and no container is configured: the banner never
// renders in that case, so a build must not fail over a feature that is switched
// off. With tags configured the copy is required and the failure stays loud -
// a banner with no policy behind it would be worse than no build.
export const useConsentLabels = (language: string): ConsentLabels | null => {
  const data = useStaticQuery<{
    allMarkdownRemark: { nodes: ConsentNode[] };
    privacyPolicies: { nodes: PrivacyPolicyNode[] };
  }>(graphql`
    query ConsentLabels {
      allMarkdownRemark(
        filter: { fields: { type: { eq: "text_blocks" }, name: { eq: "cookie_consent" } } }
      ) {
        nodes {
          fields { language }
          frontmatter {
            message
            privacy_link
            accept
            reject
            settings
            close
          }
        }
      }
      privacyPolicies: allMarkdownRemark(
        filter: { fields: { type: { eq: "text_blocks" }, name: { eq: "privacy_policy" } } }
      ) {
        nodes {
          fields { language }
          frontmatter { title }
          html
        }
      }
    }
  `);

  const node = data.allMarkdownRemark.nodes.find(({ fields }) => fields.language === language)
    || data.allMarkdownRemark.nodes.find(({ fields }) => fields.language === "en");

  const privacyPolicy = data.privacyPolicies.nodes.find(({ fields }) => fields.language === language)
    || data.privacyPolicies.nodes.find(({ fields }) => fields.language === "en");

  if (!node || !privacyPolicy) {
    if (!hasConfiguredTags()) return null;
    throw new Error(`Missing ${node ? "privacy_policy" : "cookie_consent"} text block`);
  }

  const { frontmatter } = node;
  return {
    message: frontmatter.message,
    privacyLink: frontmatter.privacy_link,
    accept: frontmatter.accept,
    reject: frontmatter.reject,
    settings: frontmatter.settings,
    // A language added to site.json before its copy is translated must still
    // render a dialog that can be closed, so this one label degrades instead.
    close: frontmatter.close || "Close",
    privacyPolicy,
  };
};
